import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useStore } from 'zustand';
import type { MediaAssetResponse, MediaFolderResponse } from '@/core/generated/api';
import { createMediaLibraryStore } from '@/entities/MediaLibrary/model/store';
import { mediaLibraryService } from '@/entities/MediaLibrary/api/mediaLibraryService';
import { saveMediaDownload } from '@/entities/MediaLibrary/model/format';
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue';
import { dateTimeFilterToBounds } from '@/shared/lib/dateTimeFilter';
import { getErrorMessage } from '@/shared/lib/errors';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import type { MediaEditMode } from './MediaEditDialog';

interface MediaEditing {
    mode: MediaEditMode;
    assets: MediaAssetResponse[];
    folder?: MediaFolderResponse;
}

/** Связывает каталог с локальным выбором, поиском и действиями */
export const useMediaLibrary = () => {
    const [store] = useState(createMediaLibraryStore);
    const data = useStore(store);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebouncedValue(search, 300);
    const [folderId, setFolderId] = useState<string>();
    const [contentType, setContentType] = useState('');
    const [unusedOnly, setUnusedOnly] = useState(false);
    const [date, setDate] = useState<string[] | null>(null);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(24);
    const [selected, setSelected] = useState<string[]>([]);
    const [editing, setEditing] = useState<MediaEditing>();
    const [deleting, setDeleting] = useState<MediaAssetResponse[]>();
    const [details, setDetails] = useState<MediaAssetResponse>();
    const [actionError, setActionError] = useState('');
    const [working, setWorking] = useState(false);
    const canView = useCanAccessPermission('admin.media-library.view');
    const bounds = dateTimeFilterToBounds(date);

    const query = useMemo(() => ({
        searchQuery: debouncedSearch || undefined,
        folderId,
        contentType: contentType || undefined,
        unusedOnly,
        includeUsage: false,
        createdFrom: bounds?.from,
        createdTo: bounds?.to,
        takeCount: pageSize,
        skipCount: page * pageSize,
        ascending: false,
    }), [debouncedSearch, folderId, contentType, unusedOnly, bounds?.from, bounds?.to, page, pageSize]);

    /** Обновляет текущую страницу каталога */
    const reload = useCallback(async (): Promise<void> => {
        if (canView) await store.getState().load(query);
    }, [store, query, canView]);

    useEffect(() => { void reload(); }, [reload]);
    useEffect(() => { setSelected([]); }, [query]);

    useEffect(() => {
        if (data.status !== RequestStatus.Success) return;

        const lastPage = Math.max(0, Math.ceil(data.allCount / pageSize) - 1);

        if (page > lastPage) setPage(lastPage);
    }, [data.status, data.allCount, page, pageSize]);

    /** Меняет папку и возвращается на первую страницу */
    const selectFolder = (id?: string): void => {
        setFolderId(id);
        setPage(0);
    };

    /** Переключает выбор одного изображения */
    const toggle = (id: string): void => setSelected(previous => previous.includes(id)
        ? previous.filter(value => value !== id)
        : previous.length < 50
            ? [...previous, id]
            : previous);

    /** Открывает редактор и закрывает панель свойств */
    const edit = (
        mode: MediaEditMode,
        assets: MediaAssetResponse[] = [],
        folder?: MediaFolderResponse
    ): void => {
        setDetails(undefined);

        setEditing({
            mode,
            assets,
            folder
        });
    };

    /** Скачивает выбранные изображения архивом */
    const download = async (assets: MediaAssetResponse[]): Promise<void> => {
        setWorking(true);
        setActionError('');

        try {
            const blob = await mediaLibraryService.download(assets.map(item => item.id ?? ''));
            saveMediaDownload(blob);
        } catch (error: unknown) {
            setActionError(getErrorMessage(error, 'Не удалось скачать изображения'));
        } finally {
            setWorking(false);
        }
    };

    /** Удаляет пустую папку после явного действия в меню */
    const deleteFolder = async (folder: MediaFolderResponse): Promise<void> => {
        if (!folder.id || !folder.version) return;
        setWorking(true);
        setActionError('');

        try {
            await mediaLibraryService.deleteFolder(folder.id, folder.version);

            if (folderId === folder.id) selectFolder(folder.parentId ?? undefined);
            await Promise.all([reload(), store.getState().loadFolders(true)]);
        } catch (error: unknown) {
            setActionError(getErrorMessage(error, 'Не удалось удалить папку'));
        } finally {
            setWorking(false);
        }
    };

    /** Обновляет каталог после изменения файлов */
    const saved = (): void => {
        if (editing?.mode === 'folder' || editing?.mode === 'rename-folder') {
            void store.getState().loadFolders(true);
        }
        setEditing(undefined);
        setDeleting(undefined);
        setDetails(undefined);
        setSelected([]);
        void reload();
    };

    return {
        ...data,
        search,
        setSearch,
        folderId,
        selectFolder,
        contentType,
        setContentType,
        unusedOnly,
        setUnusedOnly,
        date,
        setDate,
        page,
        setPage,
        pageSize,
        setPageSize,
        selected,
        setSelected,
        toggle,
        editing,
        setEditing,
        deleting,
        setDeleting,
        details,
        setDetails,
        edit,
        download,
        deleteFolder,
        actionError,
        working,
        canView,
        reload,
        /** Обновляет дерево папок по явному запросу */
        reloadFolders: () => store.getState().loadFolders(true),
        saved
    };
};
